/**
 * Tag Category Mapping Service
 * Handles fetching, caching, and managing tag-category mappings from the database
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import type { Tables } from '@/integrations/supabase/types';

// Types for the service
export interface TagCategoryMapping {
  id: string;
  tag_id: string;
  category_id: string;
  tag_name: string;
  category_name: string;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface ExtendedSolution {
  id: string;
  name: string;
  short_description: string;
  description?: string | null;
  details?: any;
  image_url?: string | null;
  featured?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  status?: string | null;
  tags?: string[];
  categories?: string[];
  slug?: string | null;
}

interface CacheEntry {
  data: TagCategoryMapping[];
  timestamp: number;
  version: string;
}

export class TagCategoryMappingService {
  private static instance: TagCategoryMappingService;
  private readonly CACHE_KEY = 'tag_category_mappings';
  private readonly CACHE_VERSION = '2.0.0'; // Updated for FK structure
  private readonly CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  private cache: TagCategoryMapping[] | null = null;

  private constructor() {}

  public static getInstance(): TagCategoryMappingService {
    if (!TagCategoryMappingService.instance) {
      TagCategoryMappingService.instance = new TagCategoryMappingService();
    }
    return TagCategoryMappingService.instance;
  }

  /**
   * Fetches tag-category mappings with proper JOIN to get tag and category names
   */
  private async fetchMappingsFromDatabase(): Promise<TagCategoryMapping[]> {
    try {
      const { data, error } = await supabase
        .from('tag_category_mappings')
        .select(`
          id,
          tag_id,
          category_id,
          is_active,
          created_at,
          updated_at,
          tags!fk_tag_id (
            name
          ),
          categories!fk_category_id (
            name
          )
        `)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching tag category mappings:', error);
        throw error;
      }

      if (!data) {
        return [];
      }

      // Transform the joined data to include tag and category names
      const mappings: TagCategoryMapping[] = data.map((item: any) => ({
        id: item.id,
        tag_id: item.tag_id,
        category_id: item.category_id,
        tag_name: item.tags?.name || '',
        category_name: item.categories?.name || '',
        is_active: item.is_active ?? true,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      return mappings;
    } catch (error) {
      console.error('Failed to fetch tag category mappings:', error);
      throw error;
    }
  }

  /**
   * Loads cached mappings from localStorage
   */
  private loadFromCache(): TagCategoryMapping[] | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const cacheEntry: CacheEntry = JSON.parse(cached);
      
      // Check version compatibility
      if (cacheEntry.version !== this.CACHE_VERSION) {
        console.log('Cache version mismatch, invalidating cache');
        this.clearCache();
        return null;
      }

      // Check if cache is still valid
      const now = Date.now();
      if (now - cacheEntry.timestamp > this.CACHE_TTL_MS) {
        console.log('Cache expired, invalidating cache');
        this.clearCache();
        return null;
      }

      return cacheEntry.data;
    } catch (error) {
      console.error('Error loading cache:', error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Saves mappings to localStorage cache
   */
  private saveToCache(mappings: TagCategoryMapping[]): void {
    try {
      const cacheEntry: CacheEntry = {
        data: mappings,
        timestamp: Date.now(),
        version: this.CACHE_VERSION,
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  }

  /**
   * Clears the cache
   */
  public clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      this.cache = null;
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Gets all tag-category mappings with caching
   */
  public async getMappings(): Promise<TagCategoryMapping[]> {
    // Try memory cache first
    if (this.cache) {
      return this.cache;
    }

    // Try localStorage cache
    const cachedMappings = this.loadFromCache();
    if (cachedMappings) {
      this.cache = cachedMappings;
      return cachedMappings;
    }

    // Fetch from database and cache
    try {
      const mappings = await this.fetchMappingsFromDatabase();
      this.cache = mappings;
      this.saveToCache(mappings);
      return mappings;
    } catch (error) {
      console.error('Failed to get mappings, returning empty array:', error);
      return [];
    }
  }

  /**
   * Gets categories that a specific tag should appear under
   */
  public async getCategoriesForTag(tagName: string): Promise<string[]> {
    const mappings = await this.getMappings();
    return mappings
      .filter(mapping => mapping.tag_name.toLowerCase() === tagName.toLowerCase())
      .map(mapping => mapping.category_name);
  }

  /**
   * Gets all tags that map to a specific category
   */
  public async getTagsForCategory(categoryName: string): Promise<string[]> {
    const mappings = await this.getMappings();
    return mappings
      .filter(mapping => mapping.category_name.toLowerCase() === categoryName.toLowerCase())
      .map(mapping => mapping.tag_name);
  }

  /**
   * Gets the set of non-Other category names for filtering
   */
  private getNonOtherCategoryNames(): Set<string> {
    return new Set(['Language', 'Image', 'Video', 'Audio', 'Multimodal']);
  }

  /**
   * Creates a lookup set of tags that map to non-Other categories
   */
  private async getTagsThatMapToNonOtherCategories(): Promise<Set<string>> {
    const allMappings = await this.getMappings();
    const nonOtherCategories = this.getNonOtherCategoryNames();
    
    const tagsThatMapToNonOtherCategories = new Set<string>();
    allMappings.forEach(mapping => {
      if (nonOtherCategories.has(mapping.category_name)) {
        tagsThatMapToNonOtherCategories.add(mapping.tag_name.toLowerCase());
      }
    });
    
    return tagsThatMapToNonOtherCategories;
  }

  /**
   * Checks if a solution matches any non-Other category
   */
  private async doesSolutionMatchNonOtherCategory(
    solution: ExtendedSolution,
    tagsThatMapToNonOtherCategories: Set<string>
  ): Promise<boolean> {
    const nonOtherCategories = this.getNonOtherCategoryNames();
    
    // Check if solution has primary categories that are not "Other"
    if (solution.categories) {
      for (const category of solution.categories) {
        if (nonOtherCategories.has(category)) {
          return true;
        }
      }
    }
    
    // Check if solution has tags that map to other categories
    if (solution.tags) {
      for (const tag of solution.tags) {
        if (tagsThatMapToNonOtherCategories.has(tag.toLowerCase())) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Checks if a solution should be explicitly included in "Other" category
   */
  private async shouldExplicitlyIncludeInOther(solution: ExtendedSolution): Promise<boolean> {
    // Check if explicitly categorized as "Other"
    if (solution.categories && solution.categories.includes('Other')) {
      return true;
    }
    
    // Check if has tags that map to "Other"
    if (solution.tags) {
      const otherTags = await this.getTagsForCategory('Other');
      const otherTagsLower = otherTags.map(tag => tag.toLowerCase());
      
      for (const tag of solution.tags) {
        if (otherTagsLower.includes(tag.toLowerCase())) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Filters solutions for the "Other" category specifically
   */
  private async filterSolutionsForOtherCategory(solutions: ExtendedSolution[]): Promise<ExtendedSolution[]> {
    const tagsThatMapToNonOtherCategories = await this.getTagsThatMapToNonOtherCategories();
    const filteredSolutionsMap = new Map<string, ExtendedSolution>();
    
    for (const solution of solutions) {
      const matchesNonOtherCategory = await this.doesSolutionMatchNonOtherCategory(
        solution, 
        tagsThatMapToNonOtherCategories
      );
      
      // Include in "Other" if:
      // 1. Doesn't match any non-Other category, OR
      // 2. Explicitly categorized as "Other", OR  
      // 3. Has tags that explicitly map to "Other"
      let shouldInclude = !matchesNonOtherCategory;
      
      if (!shouldInclude) {
        shouldInclude = await this.shouldExplicitlyIncludeInOther(solution);
      }
      
      if (shouldInclude) {
        filteredSolutionsMap.set(solution.id, solution);
      }
    }
    
    return Array.from(filteredSolutionsMap.values());
  }

  /**
   * Filters solutions by category, including those that match via tags
   */
  public async filterSolutionsByCategory(
    solutions: ExtendedSolution[],
    categoryName: string
  ): Promise<ExtendedSolution[]> {
    if (categoryName === 'All') {
      return solutions;
    }

    // Special handling for "Other" category
    if (categoryName === 'Other') {
      return await this.filterSolutionsForOtherCategory(solutions);
    }

    // Regular category filtering logic for non-Other categories
    const relevantTags = await this.getTagsForCategory(categoryName);
    const relevantTagsLower = relevantTags.map(tag => tag.toLowerCase());

    // Use Map for efficient deduplication by solution ID
    const filteredSolutionsMap = new Map<string, ExtendedSolution>();

    for (const solution of solutions) {
      let shouldInclude = false;

      // Check primary category
      if (solution.categories && solution.categories.includes(categoryName)) {
        shouldInclude = true;
      }

      // Check tags
      if (!shouldInclude && solution.tags) {
        for (const tag of solution.tags) {
          if (relevantTagsLower.includes(tag.toLowerCase())) {
            shouldInclude = true;
            break;
          }
        }
      }

      if (shouldInclude) {
        filteredSolutionsMap.set(solution.id, solution);
      }
    }

    return Array.from(filteredSolutionsMap.values());
  }

  /**
   * Refreshes the cache by fetching fresh data from the database
   */
  public async refreshCache(): Promise<TagCategoryMapping[]> {
    this.clearCache();
    return await this.getMappings();
  }

  /**
   * Adds a new tag-category mapping (admin function)
   */
  public async addMapping(tagId: string, categoryId: string): Promise<TagCategoryMapping | null> {
    try {
      const { data, error } = await supabase
        .from('tag_category_mappings')
        .insert([{ tag_id: tagId, category_id: categoryId }])
        .select(`
          id,
          tag_id,
          category_id,
          is_active,
          created_at,
          updated_at,
          tags!fk_tag_id (
            name
          ),
          categories!fk_category_id (
            name
          )
        `)
        .single();

      if (error) {
        console.error('Error adding mapping:', error);
        return null;
      }

      // Transform the response
      const mapping: TagCategoryMapping = {
        id: data.id,
        tag_id: data.tag_id,
        category_id: data.category_id,
        tag_name: (data.tags as any)?.name || '',
        category_name: (data.categories as any)?.name || '',
        is_active: data.is_active ?? true,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      // Invalidate cache to force refresh
      this.clearCache();
      
      return mapping;
    } catch (error) {
      console.error('Failed to add mapping:', error);
      return null;
    }
  }

  /**
   * Removes a tag-category mapping (admin function)
   */
  public async removeMapping(mappingId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tag_category_mappings')
        .update({ is_active: false })
        .eq('id', mappingId);

      if (error) {
        console.error('Error removing mapping:', error);
        return false;
      }

      // Invalidate cache to force refresh
      this.clearCache();
      return true;
    } catch (error) {
      console.error('Failed to remove mapping:', error);
      return false;
    }
  }
} 